import clsx from 'clsx';
import { AriaAttributes, ElementType, forwardRef, ReactNode } from 'react';

import type { TextColorProp } from '../../types/ColorProps';
import type { MarginProp, PaddingProp } from '../../types/SpacingProps';

export type Variant =
  | 'title-hero'
  | 'title-page'
  | 'title-block'
  | 'title-body'
  | 'content-body-bold'
  | 'content-body'
  | 'content-intro'
  | 'label-small'
  | 'label-regular'
  | 'label-regular-underline'
  | 'label-large'
  | 'label-large-underline';
type HTMLElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'label' | 'div' | 'span' | 'figcaption' | 'legend' | 'li' | 'td' | 'th';

type Props = AriaAttributes & {
  children?: ReactNode;
  dangerouslySetInnerHTML?: string;
  /**
   * Add margins to this piece of text.
   * Follows TailwindCSS margin classes.
   *
   * @see https://tailwindcss.com/docs/margin
   */
  marginClassname?: MarginProp;
  /**
   * Add paddings to this piece of text.
   * Follows TailwindCSS padding classes.
   *
   * @see https://tailwindcss.com/docs/padding
   */
  paddingClassname?: PaddingProp;
  textColorClassname?: TextColorProp;
  className?: string;
  type?: HTMLElementType;
  variant: Variant;
  id?: string;
  /**
   * HTMLLabelElement: htmlFor property
   *
   * The HTMLLabelElement.htmlFor property reflects the value of the for content property. That means that this script-accessible property is used to set and read the value of the content property for, which is the ID of the label's associated control element.
   */
  htmlFor?: string;
  'data-testid'?: string;
};

// Title typography
export const titleHeroClasses = clsx(
  'font-title-hero-mobile text-title-hero-mobile leading-title-hero-mobile md:font-title-hero-desktop md:text-title-hero-desktop md:leading-title-hero-desktop',
);

export const titlePageClasses = clsx(
  'font-title-page-mobile text-title-page-mobile leading-title-page-mobile md:font-title-page-desktop md:text-title-page-desktop md:leading-title-page-desktop',
);

export const titleBlockClasses = clsx(
  'font-title-block-mobile text-title-block-mobile leading-title-block-mobile md:font-title-block-desktop md:text-title-block-desktop md:leading-title-block-desktop',
);

export const titleBodyClasses = clsx(
  'font-title-body-mobile text-title-body-mobile leading-title-body-mobile md:font-title-body-desktop md:text-title-body-desktop md:leading-title-body-desktop',
);

// Content typography
export const contentBodyClasses = clsx('font-content-body-mobile text-content-body-mobile leading-content-body-mobile md:text-content-body-desktop md:leading-content-body-desktop lg:font-content-body-desktop');

export const contentBodyBoldClasses = clsx('font-content-body-bold-mobile text-content-body-mobile leading-content-body-mobile md:text-content-body-desktop md:leading-content-body-desktop lg:font-content-body-bold-desktop');

export const contentIntroClasses = clsx(
  'font-content-intro-mobile text-content-intro-mobile leading-content-intro-mobile md:text-content-intro-desktop md:leading-content-intro-desktop  lg:font-content-intro-desktop',
);

export const labelSmallClasses = clsx(
  'font-label-small-mobile text-label-small-mobile leading-label-small-mobile md:text-label-small-desktop md:leading-label-small-desktop lg:font-label-small-desktop',
);

export const labelRegularClasses = clsx(
  'font-label-regular-mobile text-label-regular-mobile leading-label-regular-mobile md:text-label-regular-desktop md:leading-label-regular-desktop lg:font-label-regular-desktop',
);

export const labelLargeClasses = clsx(
  'font-label-large-mobile text-label-large-mobile leading-label-large-mobile md:text-label-large-desktop md:leading-label-large-desktop lg:font-label-large-desktop',
);

export const variantClasses: Record<Variant, string> = {
  'title-hero': titleHeroClasses,
  'title-page': titlePageClasses,
  'title-block': titleBlockClasses,
  'title-body': titleBodyClasses,
  'content-body': contentBodyClasses,
  'content-body-bold': contentBodyBoldClasses,
  'content-intro': contentIntroClasses,
  'label-small': labelSmallClasses,
  'label-regular': labelRegularClasses,
  'label-regular-underline': `${labelRegularClasses} underline`,
  'label-large': labelLargeClasses,
  'label-large-underline': `${labelLargeClasses} underline`,
};

const variantToHTMLTypeMap: Record<Variant, HTMLElementType> = {
  'title-hero': 'h1',
  'title-page': 'h1',
  'title-block': 'h2',
  'title-body': 'h3',
  'content-body-bold': 'p',
  'content-body': 'p',
  'content-intro': 'p',
  'label-small': 'label',
  'label-regular': 'label',
  'label-regular-underline': 'label',
  'label-large': 'label',
  'label-large-underline': 'label',
};

/**
 * Typography wrapper component.
 *
 * Alle teksten moeten gewrapped worden met dit component om consistentie te waarborgen.
 */
export const Typography = forwardRef<HTMLElement, Props>(({
  children,
  htmlFor,
  type,
  variant,
  dangerouslySetInnerHTML,
  marginClassname,
  paddingClassname,
  textColorClassname,
  className,
  id,
  ...dataAttributes
}, ref) => {
  const classes = clsx(
    variantClasses[variant],
    marginClassname && typeof marginClassname === 'string' ? marginClassname : marginClassname?.join(' '),
    paddingClassname && typeof paddingClassname === 'string' ? paddingClassname : paddingClassname?.join(' '),
    textColorClassname,
    className,
  );

  const Component = (type || variantToHTMLTypeMap[variant]) as ElementType;

  return (
    <Component
      className={classes}
      dangerouslySetInnerHTML={
        dangerouslySetInnerHTML ? {
          __html: dangerouslySetInnerHTML,
        } : undefined
      }
      htmlFor={htmlFor}
      id={id}
      ref={ref}
      {...dataAttributes}
    >
      {dangerouslySetInnerHTML ? undefined : children}
    </Component>
  );
});

Typography.displayName = 'Typography';
